import { useState } from 'react';
import Layout from '@/components/feature/Layout';

export default function AboutPage() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotVal = (formData.get('phone_alt') as string || '').trim();
    if (honeypotVal) {
      setFormSubmitted(true);
      return;
    }

    try {
      const res = await fetch('https://knowguide.github.io/api/form/d9u4e9v8l97n3eas3h3g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: formData.get('name') as string,
          email: formData.get('email') as string,
          message: formData.get('message') as string,
        }).toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore parse error */ }
      if (res.ok && parsed?.code === 'OK') {
        setFormSubmitted(true);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后再试');
        } else {
          setFormError(serverMsg || '提交失败，请稍后再试');
        }
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <Layout>
      <section className="pt-24 pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 font-heading mb-4 text-center">
              关于我们
            </h1>
            <p className="text-foreground-600 text-center mb-16">
              用科学与温暖，陪伴每一位青少年的健康成长
            </p>

            <div className="space-y-20">
              <section>
                <h2 className="text-2xl font-bold text-foreground-900 font-heading mb-6">品牌故事</h2>
                <div className="space-y-4 text-foreground-700 leading-relaxed">
                  <p>
                    青春性教育成立于2024年，由一群关注青少年健康成长的医生、心理咨询师和教育工作者共同发起。我们深知，在青少年成长的关键阶段，科学性教育的缺失可能导致认知偏差、健康风险和心理困扰。
                  </p>
                  <p>
                    我们的使命是<strong>用科学严谨但温暖包容的方式，为青少年提供全面、准确的性教育知识</strong>。我们相信，知识是最好的保护，理解是最暖的陪伴。
                  </p>
                  <p>
                    网站所有内容均由具有执业资质的医学专家和心理咨询师审核，确保信息的科学性和准确性。我们坚持不贩卖焦虑、不传播谣言、不道德绑架的原则，只提供对青少年真正有用的知识。
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-foreground-900 font-heading mb-6">专业团队</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {[
                    { name: '李医生', title: '妇产科主治医师', desc: '十余年临床经验，专注青少年妇科健康与性教育科普' },
                    { name: '王教授', title: '生殖健康专家', desc: '长期从事生殖健康教育研究，发表多篇学术论文' },
                    { name: '陈心理咨询师', title: '国家二级心理咨询师', desc: '专注青少年心理发展，擅长性心理辅导与情感教育' },
                    { name: '赵医生', title: '疾控中心专家', desc: '性病艾滋病防治领域资深专家，致力于青少年健康防护' },
                  ].map((member) => (
                    <div key={member.name} className="bg-background-50 border border-background-200/70 rounded-2xl p-6">
                      <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                        <i className="ri-user-3-line text-xl text-primary-600"></i>
                      </div>
                      <h3 className="text-lg font-semibold text-foreground-900 mb-1 font-heading">{member.name}</h3>
                      <p className="text-sm text-primary-600 font-medium mb-2">{member.title}</p>
                      <p className="text-sm text-foreground-600 leading-relaxed">{member.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-foreground-900 font-heading mb-6">社会认可</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  {[
                    { icon: 'ri-award-line', stat: '50万+', label: '累计阅读量' },
                    { icon: 'ri-team-line', stat: '12位', label: '专业审核专家' },
                    { icon: 'ri-article-line', stat: '100+', label: '科普文章' },
                  ].map((item) => (
                    <div key={item.label} className="bg-background-50 border border-background-200/70 rounded-2xl p-6 text-center">
                      <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 mb-4">
                        <i className={`${item.icon} text-xl text-accent-600`}></i>
                      </div>
                      <p className="text-3xl font-bold text-foreground-900 font-heading mb-1">{item.stat}</p>
                      <p className="text-sm text-foreground-500">{item.label}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section id="contact">
                <h2 className="text-2xl font-bold text-foreground-900 font-heading mb-6">联系我们</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                        <i className="ri-mail-line text-primary-600"></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                        <p className="text-sm text-foreground-600">contact@qingchun-edu.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                        <i className="ri-phone-line text-primary-600"></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-900 mb-1">咨询热线</h3>
                        <p className="text-sm text-foreground-600">400-123-4567</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                        <i className="ri-time-line text-primary-600"></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-900 mb-1">工作时间</h3>
                        <p className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    {formSubmitted ? (
                      <div className="bg-background-100 rounded-2xl p-8 text-center">
                        <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-4">
                          <i className="ri-check-line text-2xl text-primary-600"></i>
                        </div>
                        <h3 className="text-lg font-semibold text-foreground-900 mb-2">消息已发送</h3>
                        <p className="text-sm text-foreground-600">感谢你的留言，我们会尽快回复。</p>
                      </div>
                    ) : (
                      <form
                        data-readdy-form
                        onSubmit={handleContactSubmit}
                        className="bg-background-100 rounded-2xl p-6 space-y-4"
                      >
                        <div className="honeypot-field">
                          <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                        </div>
                        <div>
                          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                            姓名
                          </label>
                          <input
                            id="contact-name"
                            type="text"
                            name="name"
                            required
                            className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200/70 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                            placeholder="请输入你的姓名"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                            邮箱
                          </label>
                          <input
                            id="contact-email"
                            type="email"
                            name="email"
                            required
                            className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200/70 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                            placeholder="请输入你的邮箱"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                            留言
                          </label>
                          <textarea
                            id="contact-message"
                            name="message"
                            required
                            maxLength={500}
                            rows={4}
                            className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200/70 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
                            placeholder="请输入你想咨询的内容（500字以内）"
                          ></textarea>
                        </div>
                        <button
                          type="submit"
                          className="w-full py-3 rounded-xl bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                        >
                          发送留言
                        </button>
                      </form>
                    )}
                    {formError && (
                      <p className="text-sm text-red-500 mt-3">{formError}</p>
                    )}
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}